import { defaults, merge, round, truncate } from 'lodash-es'
import prettyBytes, { type Options as PrettyBytesOptions } from 'pretty-bytes'

import { unref } from 'vue'

import { DateTime, type DateTimeMaybeValid, Duration } from 'luxon'
import type { AlsoRef } from '@/stores'
import dompurify from 'dompurify'
import type { ComposerTranslation } from 'vue-i18n'

interface TruncateOptions {
  length?: number
  omission?: string
  seperator?: RegExp | string
  stripHtml?: boolean
}

interface DateFormatOptions {
  year?: 'numeric' | '2-digit' | undefined
  month?: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow' | undefined
  day?: 'numeric' | '2-digit' | undefined
  weekday?: 'long' | 'short' | 'narrow' | undefined
}

interface DateTimeOptions {
  omitTodayDate?: boolean
}

interface TimeFormatOptions {
  withSeconds?: boolean
  forceDigitsEvenIfNull?: boolean
  suppressDays?: boolean
  hoursPerDay?: 24 | 8
  allowNegative?: boolean
}

interface RelativeOpts {
  withSeconds?: boolean
  threshold?: {
    ss?: number
    s?: number
    m?: number
    h?: number
    d?: number
    w?: number | null
    M?: number
  }
}

interface NumberFormatOptions {
  localeMatcher?: string | undefined
  style?: string | undefined
  currency?: string | undefined
  currencySign?: string | undefined
  useGrouping?: boolean | undefined
  minimumIntegerDigits?: number | undefined
  minimumFractionDigits?: number | undefined
  maximumFractionDigits?: number | undefined
  minimumSignificantDigits?: number | undefined
  maximumSignificantDigits?: number | undefined
}

interface ChangeDayBaseOptions {
  hoursPerDay: Required<TimeFormatOptions>['hoursPerDay']
}

const DEFAULT_TIMEFORMAT_OPTIONS: Required<TimeFormatOptions> = {
  withSeconds: true,
  forceDigitsEvenIfNull: false,
  suppressDays: false,
  hoursPerDay: 24,
  allowNegative: false
}

export class _Formatter {
  readonly #t: ComposerTranslation

  constructor(i18nFunction: ComposerTranslation) {
    this.#t = i18nFunction
  }

  Currency(value: number, options?: NumberFormatOptions): string {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', ...(options ?? {}) }).format(value)
  }

  FileSize(size: number, options?: PrettyBytesOptions): string {
    return prettyBytes(size, options)
  }

  Date(date: AlsoRef<number | string | Date | DateTime | null | undefined>, option?: DateFormatOptions): string {
    let unwrappedDate = unref(date)
    if (!unwrappedDate) return ''
    else if (DateTime.isDateTime(unwrappedDate)) {
      if (!unwrappedDate.isValid) return ''
      unwrappedDate = unwrappedDate.toJSDate()
    }
    let wd = ''
    if (option?.weekday) {
      wd = new Date(unwrappedDate).toLocaleDateString(undefined, { weekday: option.weekday }) + ', '
    }
    const opts: Intl.DateTimeFormatOptions = {
      year: option?.year ?? 'numeric',
      month: '2-digit',
      day: '2-digit'
    }
    return wd + new Date(unwrappedDate).toLocaleDateString(undefined, opts)
  }

  DateTime(date: AlsoRef<number | string | Date | DateTimeMaybeValid | null | undefined>, options?: DateTimeOptions): string {
    date = unref(date)
    const defaultOptions: Required<DateTimeOptions> = { omitTodayDate: false }
    options = merge(defaultOptions, options)
    if (!date) return ''
    if (DateTime.isDateTime(date)) {
      if (!date.isValid) return ''
      date = date.toJSDate()
    }
    const dt = new Date(date)
    let result = ''
    if (!options.omitTodayDate) {
      result += dt.toLocaleDateString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    }
    result += ' '
    result += dt.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit'
    })

    return result
  }

  DateTimeRelative(date: AlsoRef<number | string | Date | DateTime | null | undefined>, options?: RelativeOpts): string | undefined {
    date = unref(date)

    if (!date) return ''
    if (!DateTime.isDateTime(date)) {
      if (typeof date == 'number') date = DateTime.fromSeconds(date)
      else if (typeof date == 'string') date = DateTime.fromJSDate(new Date(date))
      else date = DateTime.fromJSDate(date)
    }
    if (!options) options = {}
    if (!options.threshold) options.threshold = {}
    const tsSeconds =
        (options.threshold.s ?? 0) + (options.threshold.m ?? 0) * 60 + (options.threshold.h ?? 0) * 60 * 60 + (options.threshold.d ?? 0) * 60 * 60 * 24 + (options.threshold.w ?? 0) * 60 * 60 * 24 * 7 + (options.threshold.M ?? 0) * 60 * 60 * 24 * 30
    if (DateTime.now().diff(date, 'seconds').seconds >= tsSeconds)
      return date.toLocaleString({
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: options.withSeconds ? '2-digit' : undefined
      })
    else return date.toRelative() ?? undefined
  }

  DateTimeRelativeAbstract(date: AlsoRef<number | string | Date | DateTime | null | undefined>): string | undefined {
    date = unref(date)

    if (!date) return ''
    if (!DateTime.isDateTime(date)) {
      if (typeof date == 'number') date = DateTime.fromSeconds(date)
      else if (typeof date == 'string') date = DateTime.fromJSDate(new Date(date))
      else date = DateTime.fromJSDate(date)
    }
    const diff = DateTime.now().plus({ seconds: 1 }).diff(date, 'minutes').minutes
    const isToday = date.toISODate() === DateTime.now().toISODate()
    const isYesterday = date.toISODate() === DateTime.now().minus({ day: 1 }).toISODate()
    if (diff < 0) return 'gerade aktiv'
    else if (diff <= 15) return 'gerade aktiv'
    else if (isToday) return 'kürzlich aktiv'
    else if (isYesterday) return 'gestern aktiv'
    else return `am: ${date.toFormat('dd.LL.yyyy')}`
  }

  DayDifference(date: AlsoRef<string | Date | DateTime | null | undefined>): string {
    date = unref(date)
    if (!date) return ''
    if (typeof date == 'string') date = DateTime.fromISO(date)
    else if (date instanceof Date) date = DateTime.fromJSDate(date)
    if (!date.isValid) return ''

    const pluralize = (val: number, unit: 'min' | 'hour' | 'day') => {
      switch (unit) {
        case 'min':
          return `${val} ${val == 1 ? 'Minute' : 'Minuten'}`
        case 'hour':
          return `${val} ${val == 1 ? 'Stunde' : 'Stunden'}`
        case 'day':
          return `${val} ${val == 1 ? 'Tag' : 'Tagen'}`
        default:
          break
      }
    }

    const delta = date.diffNow(['days', 'hours', 'minutes']).negate()
    if (delta.days == 0) {
      if (delta.hours == 0) return `vor ${pluralize(Math.trunc(Math.abs(delta.minutes)), 'min')}`
      else return `vor ${pluralize(Math.trunc(Math.abs(delta.hours)), 'hour')}`
    } else if (delta.days > 0) return `vor ${pluralize(Math.trunc(Math.abs(delta.days)), 'day')}`
    else return `in ${pluralize(Math.trunc(Math.abs(delta.days)), 'day')}`
  }

  TimeOnly(date: AlsoRef<number | string | Date | null | undefined>): string {
    date = unref(date)
    if (!date) return ''
    const dt = new Date(date)
    return dt.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  Time(durationOrTotalSeconds: AlsoRef<Duration | number | string | undefined | null>, options: TimeFormatOptions = {}): string {
    options = defaults(options, DEFAULT_TIMEFORMAT_OPTIONS)
    durationOrTotalSeconds = unref(durationOrTotalSeconds)
    if (!durationOrTotalSeconds) return options.withSeconds ? (options.forceDigitsEvenIfNull ? '00:00:00' : '--:--:--') : options.forceDigitsEvenIfNull ? '00:00' : '--:--'
    if (typeof durationOrTotalSeconds == 'string') {
      const durationOrTotalSecondsInt = parseInt(durationOrTotalSeconds, 10)
      if (isNaN(durationOrTotalSecondsInt)) {
        //no number - try as ISO notation
        durationOrTotalSeconds = Duration.fromISO(durationOrTotalSeconds)
        if (!durationOrTotalSeconds.isValid) throw Error(`durationOrTotalSeconds must be a valid duration`)
      } else {
        durationOrTotalSeconds = durationOrTotalSecondsInt
      }
    }
    if (typeof durationOrTotalSeconds == 'number') durationOrTotalSeconds = Duration.fromDurationLike({ seconds: durationOrTotalSeconds })
    if (!durationOrTotalSeconds.isValid) return options.withSeconds ? (options.forceDigitsEvenIfNull ? '00:00:00' : '--:--:--') : options.forceDigitsEvenIfNull ? '00:00' : '--:--'

    if (durationOrTotalSeconds.shiftTo('seconds').as('seconds') === 0) return options.withSeconds ? '00:00:00' : '00:00'
    if (!options.suppressDays && durationOrTotalSeconds >= Duration.fromDurationLike({ hours: options.hoursPerDay })) {
      return this.#changeDayBase(durationOrTotalSeconds, { hoursPerDay: options.hoursPerDay! }).toFormat('d.hh:mm' + (options.withSeconds ? ':ss' : ''))
    }
    return durationOrTotalSeconds.toFormat('hh:mm' + (options.withSeconds ? ':ss' : ''))
  }

  TimeLong(durationOrTotalSeconds: AlsoRef<Duration | number | string | undefined | null>, options: TimeFormatOptions = {}): string {
    options = defaults(options, DEFAULT_TIMEFORMAT_OPTIONS)
    durationOrTotalSeconds = unref(durationOrTotalSeconds)
    if (!durationOrTotalSeconds) return options.withSeconds ? (options.forceDigitsEvenIfNull ? '0 Tage 0 Std. 0 Min. 0 Sek.' : '--:--:--') : options.forceDigitsEvenIfNull ? '0 Std. 0 Min.' : '--:--'
    if (typeof durationOrTotalSeconds == 'string') {
      const durationOrTotalSecondsInt = parseInt(durationOrTotalSeconds, 10)
      if (isNaN(durationOrTotalSecondsInt)) {
        //no number - try as ISO notation
        durationOrTotalSeconds = Duration.fromISO(durationOrTotalSeconds)
        if (!durationOrTotalSeconds.isValid) throw Error(`durationOrTotalSeconds must be a valid duration`)
      } else {
        durationOrTotalSeconds = durationOrTotalSecondsInt
      }
    }
    if (typeof durationOrTotalSeconds == 'number') durationOrTotalSeconds = Duration.fromDurationLike({ seconds: durationOrTotalSeconds })
    if (!durationOrTotalSeconds.isValid) return options.withSeconds ? (options.forceDigitsEvenIfNull ? '0 Tage 0 Std. 0 Min. 0 Sek.' : '--:--:--') : options.forceDigitsEvenIfNull ? '0 Std. 0 Min.' : '--:--'
    if (durationOrTotalSeconds.shiftTo('seconds').as('seconds') === 0) return options.withSeconds ? '0 Tage 0 Std. 0 Min.' : '0 Std. 0 Min.'
    if (!options.suppressDays && durationOrTotalSeconds >= Duration.fromDurationLike({ hours: options.hoursPerDay })) {
      return this.#changeDayBase(durationOrTotalSeconds, { hoursPerDay: options.hoursPerDay! }).toFormat('d \'Tage\' h \'Std.\' mm \'Min.\'' + (options.withSeconds ? ' ss \'Sek.\'' : ''))
    }
    return durationOrTotalSeconds.toFormat('h \'Std.\' mm \'Min.\'' + (options.withSeconds ? ' ss \'Sek.\'' : ''))
  }

  NumberFixed(num: AlsoRef<number>, digits = 2, fixed?: number): string {
    return round(unref(num), digits).toFixed(fixed ?? digits)
  }

  Number(num: AlsoRef<number>, digits = 2): number {
    return round(unref(num), digits)
  }

  SanitizeHtml(value: string | undefined | null): string | undefined {
    if (!value) return undefined
    const dompurifyInstance = dompurify()
    return dompurifyInstance.sanitize(value, { USE_PROFILES: { html: true } })
  }

  StripHTML(value: string | undefined): string | undefined {
    if (!value) return value
    const div = document.createElement('div')
    div.innerHTML = value
    const text = div.textContent ? div.textContent : div.innerText ? div.innerText : ''
    div.remove()
    return text
  }

  Truncate(input: AlsoRef<string | null | undefined>, options: TruncateOptions | undefined = undefined): string {
    input = unref(input)
    if (input === null || input === undefined) return ''

    if (options?.stripHtml) {
      input = this.StripHTML(input)
    }
    return truncate(input, options)
  }

  DayNameByDate(date: AlsoRef<string | DateTime | undefined | null>): string | undefined {
    if (!date) return
    date = unref(date)
    if (!date) return
    return (typeof date === 'string' ? DateTime.fromISO(date) : date).toLocaleString({ weekday: 'long' })
  }

  MonthNameByDate(date: AlsoRef<string | DateTime | undefined | null>): string | undefined {
    if (!date) return
    date = unref(date)
    if (!date) return
    return (typeof date === 'string' ? DateTime.fromISO(date) : date).toLocaleString({ month: 'long' })
  }

  GetPriority(value: AlsoRef<number | undefined>): string {
    value = unref(value)
    if (typeof value === 'undefined') return ''

    switch (value) {
      case 0:
        return this.#t('PriorityLow')
      case 10:
        return this.#t('PriorityNormal')
      case 20:
        return this.#t('PriorityHigh')
      case 30:
        return this.#t('PriorityUrgent')
      default:
        return this.#t('PriorityUnset') + `(${value})`
    }
  }

  #changeDayBase(input: Duration, options: ChangeDayBaseOptions = { hoursPerDay: 8 }): Duration {
    if (!input.isValid || input.toMillis() === 0 || input.as('hours') < options.hoursPerDay) return input

    const totalHours = input.as('hours')
    const days = Math.floor(totalHours / options.hoursPerDay)
    const remainingHours = totalHours % options.hoursPerDay

    // Restliche Komponenten extrahieren
    const rest = input.shiftTo('hours', 'minutes', 'seconds', 'milliseconds').toObject()

    return Duration.fromObject({
      days,
      hours: Math.trunc(remainingHours),
      minutes: rest.minutes ?? 0,
      seconds: rest.seconds ?? 0,
      milliseconds: rest.milliseconds ?? 0
    }).rescale()
  }
}

let _formatter: _Formatter | undefined

export function useFormatter(): _Formatter {
  return _formatter as _Formatter
}

export function setupFormatter(i18nFunction: ComposerTranslation) {
  if (_formatter) return _formatter
  _formatter = new _Formatter(i18nFunction)
  return _formatter
}
